import { useChartStyleButtons } from '@/composables/useChartStyleButtons';
import { isValidChartStyleRecid } from '@/composables/isValidChartStyleRecid';
import { buildUrlString } from '@/utils/buildUrlString';
import { isValidNumber } from '@/utils/isValidNumber';
import { extractStyleCacheId } from '@/utils/extractStyleCacheId';
import { sortObjectsClosure } from '@/utils/sortObjectsClosure';
import { JsonLayerChartStyle } from '@/interfaces/json-layer-responses/jsonLayerChartStyle';
import { SimpleFrontendChartStyle } from '@/interfaces/chartStyle';
import { stripHtmlFromString } from '@/plugins/vue-sanitize-html';

export function parseChartStyle(rawChartStyle: JsonLayerChartStyle): SimpleFrontendChartStyle | null {
  const {
    CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTIONS,
    DEFAULT_CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTION,
    MAX_NUM_CHART_STYLE_BUTTON_LABEL_CHARACTERS
  } = useChartStyleButtons();

  if (!isValidChartStyleRecid(rawChartStyle.recID, false)) {
    window.sc3WorkbenchLogger.error(`Unable to parse ChartStyle - rawChartStyle.recID is not a valid ChartStyle recid: ${rawChartStyle.recID}.`);
    return null;
  }

  if (!rawChartStyle.description.trim()) {
    window.sc3WorkbenchLogger.error(`Unable to parse ChartStyle with recid: ${rawChartStyle.recID} - trimmed rawChartStyle.description is an empty string.`);
    return null;
  }

  const styleCacheId = extractStyleCacheId(rawChartStyle.styleID);

  if (!styleCacheId) {
    window.sc3WorkbenchLogger.error(`Unable to parse ChartStyle with recid: ${rawChartStyle.recID} - rawChartStyle.styleID is not a valid stylecache id: ${rawChartStyle.styleID}.`);
    return null;
  }

  if (!styleCacheId.startsWith('p')) {
    window.sc3WorkbenchLogger.error(`Unable to parse ChartStyle with recid: ${rawChartStyle.recID} - rawChartStyle.styleID is a valid stylecache id but does not start with a "p": ${rawChartStyle.styleID}.`);
    return null;
  }

  const name = rawChartStyle.description.trim();
  const htmlSanitizedName = stripHtmlFromString(name);
  const searchTags = [name];
  const thumbnailUrlParameters = { i: styleCacheId };
  const thumbnailUrl = buildUrlString('/c-sc/sc', { areParametersAlreadyUrlEncoded: true, parameters: thumbnailUrlParameters });
  const recid = Number(rawChartStyle.recID);
  let chartStyleButtonIndex = -1;
  let potentialChartStyleButtonPosition = rawChartStyle.location;

  if (typeof potentialChartStyleButtonPosition === 'string') {
    potentialChartStyleButtonPosition = potentialChartStyleButtonPosition.trim().replace(/,/g, '');
  }

  if (isValidNumber(potentialChartStyleButtonPosition, { isRequiredToBeInteger: true, isRequiredToBeGreaterThanZero: true })) {
    chartStyleButtonIndex = Number(potentialChartStyleButtonPosition) - 1;
  }

  let chartStyleButtonBackgroundColorOption = DEFAULT_CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTION;
  const desiredColor = typeof rawChartStyle.color === 'string' ? rawChartStyle.color.replace(/\s/g, '').toUpperCase() : '';

  if (desiredColor) {
    chartStyleButtonBackgroundColorOption = CHART_STYLE_BUTTON_BACKGROUND_COLOR_OPTIONS.find(option => option.hexCode.replace(/\s/g, '').toUpperCase() === desiredColor) ?? chartStyleButtonBackgroundColorOption;
  }

  let chartStyleButtonLabel = '';

  if (typeof rawChartStyle.label === 'string') {
    chartStyleButtonLabel = rawChartStyle.label.replace(/\s/g, '').slice(0, MAX_NUM_CHART_STYLE_BUTTON_LABEL_CHARACTERS);
  }

  return {
    name,
    htmlSanitizedName,
    recid,
    searchTags,
    styleCacheId,
    styleCacheSettingsOverrides: {},
    temporaryName: name,
    thumbnailUrl,
    chartStyleButtonIndex,
    chartStyleButtonBackgroundColorHexCode: chartStyleButtonBackgroundColorOption.hexCode,
    chartStyleButtonTextColorHexCode: chartStyleButtonBackgroundColorOption.isDarkColor ? '#ffffff' : '#000000',
    chartStyleButtonLabel,
    description: '',
    isForPayingMembersOnly: true
  };
}

function removeGapsInChartStyleButtonIndexes(chartStyles: SimpleFrontendChartStyle[]): SimpleFrontendChartStyle[] {
  const { MAX_NUMBER_OF_CHART_STYLE_BUTTONS } = useChartStyleButtons();

  const chartStylesCopy = [ ...chartStyles ];

  chartStylesCopy.sort(sortObjectsClosure({
    propertyName: 'chartStyleButtonIndex',
    order: 'ascending'
  }));

  let chartStyleButtonChartStyles = chartStylesCopy.filter(chartStyle => chartStyle.chartStyleButtonIndex >= 0);
  const chartStyleButtonIndexesStringBeforeParsing = chartStyleButtonChartStyles.map(chartStyle => chartStyle.chartStyleButtonIndex).join(',');

  if (chartStyleButtonChartStyles.length > MAX_NUMBER_OF_CHART_STYLE_BUTTONS) {
    window.sc3WorkbenchLogger.warn(`Number of ChartStyle StyleButtons: ${chartStyleButtonChartStyles.length} is greater than the maximum number of ChartStyle StyleButtons allowed: ${MAX_NUMBER_OF_CHART_STYLE_BUTTONS}.`);
  }

  chartStyleButtonChartStyles.forEach((chartStyle, i) => {
    chartStyle.chartStyleButtonIndex = i < MAX_NUMBER_OF_CHART_STYLE_BUTTONS ? i : -1;
  });

  chartStyleButtonChartStyles = chartStyleButtonChartStyles.filter(chartStyle => chartStyle.chartStyleButtonIndex >= 0);
  const chartStyleButtonIndexesStringAfterParsing = chartStyleButtonChartStyles.map(chartStyle => chartStyle.chartStyleButtonIndex).join(',');

  if (chartStyleButtonIndexesStringBeforeParsing !== chartStyleButtonIndexesStringAfterParsing) {
    window.sc3WorkbenchLogger.warn(`ChartStyle StyleButton indexes after parsing: ${chartStyleButtonIndexesStringAfterParsing} are different from those before parsing: ${chartStyleButtonIndexesStringBeforeParsing}.`);
  }

  return chartStyles;
}

export function parseChartStylesArray(rawChartStyles: JsonLayerChartStyle[]): SimpleFrontendChartStyle[] {
  if (!Array.isArray(rawChartStyles)) {
    throw new Error(`Unable to parse ChartStyles - rawChartStyles is not an array: ${typeof rawChartStyles} ${rawChartStyles}.`);
  }

  const chartStyles: SimpleFrontendChartStyle[] = [];

  for (const rawChartStyle of rawChartStyles) {
    const chartStyle = parseChartStyle(rawChartStyle);

    if (chartStyle) {
      chartStyles.push(chartStyle);
    }
  }

  return removeGapsInChartStyleButtonIndexes(chartStyles);
}
